import * as i1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { ElementRef, Component, ChangeDetectionStrategy, Inject, Input, HostBinding, NgModule } from '@angular/core';
import { tuiCreateToken, tuiProvideOptions, tuiIsSafari, tuiBlurNativeFocused, tuiIsNativeFocusedIn, TUI_IS_IOS } from '@taiga-ui/cdk';
import { tuiSizeBigger } from '@taiga-ui/core/utils/miscellaneous';
import * as i2 from '@tinkoff/ng-polymorpheus';
import { PolymorpheusModule } from '@tinkoff/ng-polymorpheus';

/** Default values for the loader options. */
function TuiLoaderComponent_div_2_div_3_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const text_r3 = ctx.polymorpheusOutlet;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", text_r3, " ");
  }
}
function TuiLoaderComponent_div_2_div_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵnamespaceSVG();
    i0.ɵɵnamespaceHTML();
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵtemplate(1, TuiLoaderComponent_div_2_div_3_ng_container_1_Template, 2, 1, "ng-container", 7);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵclassProp("t-text_horizontal", ctx_r1.isHorizontal);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("polymorpheusOutlet", ctx_r1.textContent);
  }
}
function TuiLoaderComponent_div_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵnamespaceSVG();
    i0.ɵɵelementStart(1, "svg", 3);
    i0.ɵɵelement(2, "circle", 4);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, TuiLoaderComponent_div_2_div_3_Template, 2, 3, "div", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("t-loader_horizontal", ctx_r0.isHorizontal)("t-loader_inherit-color", ctx_r0.inheritColor);
    i0.ɵɵattribute("data-size", ctx_r0.size);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngIf", ctx_r0.hasText);
  }
}
const _c0 = ["*"];
const TUI_LOADER_DEFAULT_OPTIONS = {
  size: `m`,
  inheritColor: false,
  overlay: false
};
/**
 * Default parameters for loader component
 */
const TUI_LOADER_OPTIONS = tuiCreateToken(TUI_LOADER_DEFAULT_OPTIONS);
function tuiLoaderOptionsProvider(options) {
  return tuiProvideOptions(TUI_LOADER_OPTIONS, options, TUI_LOADER_DEFAULT_OPTIONS);
}
let TuiLoaderComponent = /*#__PURE__*/(() => {
  class TuiLoaderComponent {
    constructor(doc, el, isIos, options) {
      this.doc = doc;
      this.el = el;
      this.isIos = isIos;
      this.options = options;
      this.size = this.options.size;
      this.inheritColor = this.options.inheritColor;
      this.overlay = this.options.overlay;
      this.loading = true;
      this.isApple = tuiIsSafari(this.el.nativeElement) || this.isIos;
    }
    set showLoader(value) {
      // @bad TODO: https://github.com/angular/angular/issues/32083 think of a better way
      if (value && this.focused) {
        tuiBlurNativeFocused(this.doc);
      }
      this.loading = value;
    }
    get hasOverlay() {
      return this.overlay && this.loading;
    }
    get hasText() {
      return !!this.textContent;
    }
    get isHorizontal() {
      return !tuiSizeBigger(this.size);
    }
    get focused() {
      return tuiIsNativeFocusedIn(this.el.nativeElement);
    }
  }
  TuiLoaderComponent.ɵfac = function TuiLoaderComponent_Factory(t) {
    return new (t || TuiLoaderComponent)(i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(ElementRef), i0.ɵɵdirectiveInject(TUI_IS_IOS), i0.ɵɵdirectiveInject(TUI_LOADER_OPTIONS));
  };
  TuiLoaderComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: TuiLoaderComponent,
    selectors: [["tui-loader"]],
    hostVars: 2,
    hostBindings: function TuiLoaderComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵclassProp("_loading", ctx.loading);
      }
    },
    inputs: {
      size: "size",
      inheritColor: "inheritColor",
      overlay: "overlay",
      textContent: "textContent",
      showLoader: "showLoader"
    },
    ngContentSelectors: _c0,
    decls: 3,
    vars: 7,
    consts: [[1, "t-content", 3, "disabled"], ["class", "t-loader", 3, "t-loader_horizontal", "t-loader_inherit-color", 4, "ngIf"], [1, "t-loader"], ["automation-id", "tui-loader__loader", "focusable", "false", "viewBox", "0 0 100 100", "xmlns", "http://www.w3.org/2000/svg", 1, "t-icon"], ["cx", "50", "cy", "50", "r", "50", "stroke-dasharray", "314", 1, "t-circle"], ["automation-id", "tui-loader__text", "class", "t-text", 3, "t-text_horizontal", 4, "ngIf"], ["automation-id", "tui-loader__text", 1, "t-text"], [4, "polymorpheusOutlet"]],
    template: function TuiLoaderComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "fieldset", 0);
        i0.ɵɵprojection(1);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(2, TuiLoaderComponent_div_2_Template, 4, 6, "div", 1);
      }
      if (rf & 2) {
        i0.ɵɵclassProp("t-content_has-overlay", ctx.hasOverlay)("t-content_loading", ctx.loading);
        i0.ɵɵproperty("disabled", ctx.loading && !ctx.isApple);
        i0.ɵɵattribute("inert", ctx.loading || null);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.loading);
      }
    },
    dependencies: [i1.NgIf, i2.PolymorpheusOutletDirective],
    styles: ["@keyframes _ngcontent-%COMP%_tuiLoaderDashOffset{0%{stroke-dashoffset:-66}50%{stroke-dashoffset:-314}to{stroke-dashoffset:-66}}@keyframes _ngcontent-%COMP%_tuiLoaderRotate{0%{transform:rotate(0)}50%{transform:rotate(1turn)}to{transform:rotate(3turn)}}[_nghost-%COMP%]{position:relative;display:flex;min-width:1.5rem}._loading[_nghost-%COMP%]{overflow:hidden}.t-content[_ngcontent-%COMP%]{z-index:0;min-width:100%;height:100%;padding:0;margin:0;border:none}.t-content_has-overlay[_ngcontent-%COMP%]{opacity:.3}.t-content_loading[_ngcontent-%COMP%]{pointer-events:none}.t-loader[_ngcontent-%COMP%]{position:relative;left:-100%;display:flex;flex-direction:column;align-items:center;justify-content:center;min-width:100%;flex-shrink:0;align-self:center;color:var(--tui-text-01);stroke:var(--tui-primary);animation:tuiFadeIn var(--tui-duration)}.t-loader.t-loader_horizontal[_ngcontent-%COMP%]{flex-direction:row}.t-loader.t-loader_inherit-color[_ngcontent-%COMP%]{color:inherit;stroke:currentColor}.t-loader[data-size=xs][_ngcontent-%COMP%]{font-size:.75rem;stroke-width:38}.t-loader[data-size=s][_ngcontent-%COMP%]{font-size:1rem;stroke-width:25}.t-loader[data-size=m][_ngcontent-%COMP%]{font-size:1.5rem;stroke-width:17}.t-loader[data-size=l][_ngcontent-%COMP%]{font-size:2.5rem;stroke-width:15}.t-loader[data-size=xl][_ngcontent-%COMP%]{font-size:3.5rem;stroke-width:14}.t-loader[data-size=xxl][_ngcontent-%COMP%]{font-size:5rem;stroke-width:10}.t-text[_ngcontent-%COMP%]{font:var(--tui-font-text-s);color:var(--tui-text-01);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1rem;color:inherit;max-width:100%;text-align:center;stroke-width:0}.t-text_horizontal[_ngcontent-%COMP%]{margin:0 0 0 1rem}.t-icon[_ngcontent-%COMP%]{display:block;min-width:1em;max-width:1em;min-height:1em;max-height:1em;margin:.25rem -.5em;border-radius:100%;overflow:hidden;transform:scaleY(-1);animation:_ngcontent-%COMP%_tuiLoaderRotate 3s linear infinite}@supports (-webkit-hyphens: none){.t-icon[_ngcontent-%COMP%]{overflow:visible}}.t-circle[_ngcontent-%COMP%]{fill:none;stroke:inherit;stroke-width:inherit;animation:_ngcontent-%COMP%_tuiLoaderDashOffset 3s linear infinite}"],
    changeDetection: 0
  });
  return TuiLoaderComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiLoaderModule = /*#__PURE__*/(() => {
  class TuiLoaderModule {}
  TuiLoaderModule.ɵfac = function TuiLoaderModule_Factory(t) {
    return new (t || TuiLoaderModule)();
  };
  TuiLoaderModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: TuiLoaderModule
  });
  TuiLoaderModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    imports: [[CommonModule, PolymorpheusModule]]
  });
  return TuiLoaderModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_LOADER_DEFAULT_OPTIONS, TUI_LOADER_OPTIONS, TuiLoaderComponent, TuiLoaderModule, tuiLoaderOptionsProvider };
